import { useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Play, 
  Trash2, 
  FileImage,
  CheckCircle,
  AlertCircle,
  Loader2,
  FolderOpen,
  AlertTriangle,
  FolderInput
} from 'lucide-react';
import FeatureGuard from '../components/FeatureGuard';

function FileRenameContent() {
  const [directoryHandle, setDirectoryHandle] = useState(null);
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [results, setResults] = useState(null);

  // Check if File System Access API is supported
  const isSupported = 'showDirectoryPicker' in window;

  // Analyze files and group by barcode
  const analyzeFiles = useCallback((fileList) => {
    const groups = {};
    const invalidFiles = [];

    fileList.forEach(file => {
      // Match pattern: barcode_(number).jpg
      const match = file.name.match(/^(.+)_\((\d+)\)\.jpg$/i);
      
      if (match) {
        const barcode = match[1];
        if (!groups[barcode]) {
          groups[barcode] = [];
        }
        groups[barcode].push({
          file,
          handle: file.handle,
          originalName: file.name,
          originalNumber: parseInt(match[2])
        });
      } else if (file.name.toLowerCase().endsWith('.jpg')) {
        invalidFiles.push(file);
      }
    });

    // Sort each group by original number and assign new numbers
    const preview = {
      groups: {},
      errorFiles: [],
      validFiles: [],
      invalidFiles
    };

    Object.keys(groups).forEach(barcode => {
      const items = groups[barcode].sort((a, b) => a.originalNumber - b.originalNumber);
      
      items.forEach((item, index) => {
        const newNumber = index + 1;
        const newName = `${barcode}_(${newNumber}).jpg`;
        
        if (newNumber > 3) {
          preview.errorFiles.push({
            ...item,
            barcode,
            newName,
            reason: '3\'ten fazla dosya'
          });
        } else {
          preview.validFiles.push({
            ...item,
            barcode,
            newName,
            renamed: item.originalName !== newName
          });
        }
      });

      preview.groups[barcode] = items.length;
    });

    return preview;
  }, []);

  const handleSelectFolder = async () => {
    if (!isSupported) {
      toast.error('Tarayıcınız klasör seçimini desteklemiyor. Chrome veya Edge kullanın.');
      return;
    }

    try {
      const dirHandle = await window.showDirectoryPicker({
        mode: 'readwrite'
      });
      
      setDirectoryHandle(dirHandle);
      
      // Read all JPG files from directory
      const fileList = [];
      for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file' && entry.name.toLowerCase().endsWith('.jpg')) {
          const file = await entry.getFile();
          fileList.push({
            name: entry.name,
            file,
            handle: entry
          });
        }
      }

      if (fileList.length === 0) {
        toast.error('Klasörde JPG dosyası bulunamadı');
        return;
      }

      setFiles(fileList);
      const preview = analyzeFiles(fileList);
      setPreviewData(preview);
      setResults(null);
      toast.success(`${fileList.length} JPG dosyası bulundu`);
      
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Klasör seçme hatası:', err);
        toast.error('Klasör seçilemedi');
      }
    }
  };

  const handleProcess = async () => {
    if (!directoryHandle || !previewData) {
      toast.error('Lütfen önce bir klasör seçin');
      return;
    }

    setProcessing(true);
    const processResults = {
      renamed: [],
      movedToError: [],
      errors: []
    };

    try {
      // Create "hata" folder if needed
      let errorFolderHandle = null;
      if (previewData.errorFiles.length > 0) {
        try {
          errorFolderHandle = await directoryHandle.getDirectoryHandle('hata', { create: true });
        } catch (err) {
          console.error('Hata klasörü oluşturulamadı:', err);
        }
      }

      // Process error files first (move to hata folder)
      for (const item of previewData.errorFiles) {
        try {
          if (errorFolderHandle) {
            // Read file content
            const file = await item.handle.getFile();
            const content = await file.arrayBuffer();
            
            // Create file in hata folder
            const newFileHandle = await errorFolderHandle.getFileHandle(item.originalName, { create: true });
            const writable = await newFileHandle.createWritable();
            await writable.write(content);
            await writable.close();
            
            // Delete original
            await directoryHandle.removeEntry(item.originalName);
            
            processResults.movedToError.push(item.originalName);
          }
        } catch (err) {
          processResults.errors.push(`${item.originalName}: ${err.message}`);
        }
      }

      // Process valid files (rename)
      // First, rename to temp names to avoid conflicts
      const tempNames = [];
      for (const item of previewData.validFiles) {
        if (item.renamed) {
          try {
            const tempName = `__temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${item.originalName}`;
            const file = await item.handle.getFile();
            const content = await file.arrayBuffer();
            
            // Create temp file
            const tempHandle = await directoryHandle.getFileHandle(tempName, { create: true });
            const writable = await tempHandle.createWritable();
            await writable.write(content);
            await writable.close();
            
            // Delete original
            await directoryHandle.removeEntry(item.originalName);
            
            tempNames.push({
              tempName,
              newName: item.newName,
              originalName: item.originalName
            });
          } catch (err) {
            processResults.errors.push(`${item.originalName}: ${err.message}`);
          }
        }
      }

      // Now rename temp files to final names
      for (const { tempName, newName, originalName } of tempNames) {
        try {
          const tempHandle = await directoryHandle.getFileHandle(tempName);
          const file = await tempHandle.getFile();
          const content = await file.arrayBuffer();
          
          // Create final file
          const finalHandle = await directoryHandle.getFileHandle(newName, { create: true });
          const writable = await finalHandle.createWritable();
          await writable.write(content);
          await writable.close();
          
          // Delete temp
          await directoryHandle.removeEntry(tempName);
          
          processResults.renamed.push(`${originalName} → ${newName}`);
        } catch (err) {
          processResults.errors.push(`${tempName}: ${err.message}`);
        }
      }

      setResults(processResults);
      
      if (processResults.errors.length === 0) {
        toast.success(`İşlem tamamlandı! ${processResults.renamed.length} dosya yeniden adlandırıldı.`);
      } else {
        toast.warning(`İşlem tamamlandı ancak ${processResults.errors.length} hata oluştu.`);
      }

      // Refresh file list
      const fileList = [];
      for await (const entry of directoryHandle.values()) {
        if (entry.kind === 'file' && entry.name.toLowerCase().endsWith('.jpg')) {
          const file = await entry.getFile();
          fileList.push({
            name: entry.name,
            file,
            handle: entry
          });
        }
      }
      setFiles(fileList);
      setPreviewData(analyzeFiles(fileList));
      
    } catch (err) {
      console.error('İşlem hatası:', err);
      toast.error('İşlem sırasında hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  const handleClear = () => {
    setDirectoryHandle(null);
    setFiles([]);
    setPreviewData(null);
    setResults(null);
    toast.info('Temizlendi');
  };

  const matchedCount = previewData?.validFiles?.filter(p => p.renamed).length || 0;
  const totalValid = previewData?.validFiles?.length || 0;
  const errorCount = previewData?.errorFiles?.length || 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="file-rename-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          İsim Değiştir
        </h1>
        <p className="text-gray-600 mt-2">
          JPG dosyalarını barkod bazlı gruplandırıp yeniden numaralandırın
        </p>
      </div>

      {!isSupported && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <p>Tarayıcınız klasör erişimini desteklemiyor. Lütfen <strong>Chrome</strong> veya <strong>Edge</strong> kullanın.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Folder Selection */}
      <Card className="shadow-lg border-0 bg-white/80 backdrop-blur">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FolderInput className="w-5 h-5 text-purple-500" />
            Klasör Seçimi
          </CardTitle>
          <CardDescription>
            JPG dosyalarının bulunduğu klasörü seçin
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button
              onClick={handleSelectFolder}
              disabled={!isSupported || processing}
              className="w-full h-24 border-2 border-dashed border-purple-200 bg-white hover:bg-purple-50 hover:border-purple-400 text-gray-600"
              variant="outline"
              data-testid="select-folder-btn"
            >
              <div className="flex flex-col items-center gap-2">
                <FolderOpen className="w-8 h-8 text-purple-400" />
                <span>{directoryHandle ? 'Farklı Klasör Seç' : 'Klasör Seç'}</span>
              </div>
            </Button>

            {directoryHandle && (
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FolderOpen className="w-4 h-4 text-purple-600" />
                  <span className="font-medium text-purple-700">{directoryHandle.name}</span>
                </div>
                <p className="text-sm text-purple-600">{files.length} JPG dosyası bulundu</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {previewData && (
        <Card className="mt-6 shadow-lg border-0 bg-white/80 backdrop-blur">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileImage className="w-5 h-5 text-blue-500" />
              Önizleme
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-700 font-medium">
                    {totalValid} geçerli dosya ({matchedCount} yeniden adlandırılacak)
                  </span>
                </div>
                {errorCount > 0 && (
                  <div className="flex items-center gap-2 bg-orange-50 px-4 py-2 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <span className="text-sm text-orange-700 font-medium">
                      {errorCount} dosya "hata" klasörüne taşınacak
                    </span>
                  </div>
                )}
              </div>

              {/* Barcode Groups */}
              {Object.keys(previewData.groups).length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <p className="text-sm font-medium text-gray-600">Barkod Grupları</p>
                  </div>
                  <div className="max-h-48 overflow-y-auto divide-y">
                    {Object.entries(previewData.groups).map(([barcode, count]) => (
                      <div 
                        key={barcode} 
                        className={`px-4 py-2 flex items-center justify-between ${
                          count > 3 ? 'bg-orange-50' : 'bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <FileImage className="w-4 h-4 text-purple-500" />
                          <span className="text-sm font-mono">{barcode}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${count > 3 ? 'text-orange-600' : 'text-gray-600'}`}>
                            {count} dosya
                          </span>
                          {count > 3 && (
                            <span className="text-xs text-orange-500">
                              ({count - 3} hata klasörüne)
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results && (
        <Card className="mt-6 shadow-lg border-0 bg-white/80 backdrop-blur">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-green-600">
              <CheckCircle className="w-5 h-5" />
              İşlem Sonuçları
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {results.renamed.length > 0 && (
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-green-700 mb-2">
                    ✅ {results.renamed.length} dosya yeniden adlandırıldı
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {results.renamed.slice(0, 10).map((item, idx) => (
                      <p key={idx} className="text-xs text-green-600 font-mono">{item}</p>
                    ))}
                    {results.renamed.length > 10 && (
                      <p className="text-xs text-green-500">+{results.renamed.length - 10} daha...</p>
                    )}
                  </div>
                </div>
              )}
              {results.movedToError.length > 0 && (
                <div className="bg-orange-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-orange-700 mb-2">
                    ⚠️ {results.movedToError.length} dosya "hata" klasörüne taşındı
                  </p>
                </div>
              )}
              {results.errors.length > 0 && (
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-700 mb-2">
                    ❌ {results.errors.length} hata
                  </p>
                  <div className="max-h-24 overflow-y-auto">
                    {results.errors.map((err, idx) => (
                      <p key={idx} className="text-xs text-red-600">{err}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <Button
          onClick={handleProcess}
          disabled={!directoryHandle || matchedCount === 0 || processing}
          className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg"
          data-testid="process-button"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              İşleniyor...
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              İsimleri Değiştir
            </>
          )}
        </Button>

        <Button
          variant="outline"
          onClick={handleClear}
          disabled={processing}
          className="hover:bg-red-50 hover:text-red-600 hover:border-red-300"
          data-testid="clear-button"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Temizle
        </Button>
      </div>

      {/* Help */}
      <Card className="mt-6 shadow-lg border-0 bg-gradient-to-r from-blue-50 to-purple-50">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-gray-800 mb-3">Nasıl Çalışır?</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p>1. <strong>Klasör Seçin:</strong> JPG dosyalarının bulunduğu klasörü seçin</p>
            <p>2. <strong>Önizleme:</strong> Sistem dosyaları barkoda göre gruplar ve önizleme gösterir</p>
            <p>3. <strong>İsimleri Değiştir:</strong> Dosyalar doğrudan klasörde yeniden adlandırılır</p>
            <p>4. <strong>Hata Klasörü:</strong> 3'ten fazla olan dosyalar otomatik olarak "hata" klasörüne taşınır</p>
          </div>
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>Not:</strong> Bu özellik dosyaları doğrudan klasörünüzde değiştirir. İndirme gerektirmez.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// Wrapper with FeatureGuard
export default function FileRename() {
  return (
    <FeatureGuard featureKey="file_rename" pageName="İsim Değiştir">
      <FileRenameContent />
    </FeatureGuard>
  );
}
